import PropTypes from 'prop-types';
import { useMemo, useState, useContext, useCallback, createContext } from 'react';

import { useAuthContext } from 'src/auth/hooks';

import { CHANGELOG } from './changelog-data';
import { signupDay, entryItems, unseenItems } from './changelog-utils';

// ----------------------------------------------------------------------

// Which What's new updates the signed-in seller has not seen yet. The top bar button shows a dot while there
// are any, the dashboard announces new headliners once, and opening the page marks everything seen.
//
// Each browser remembers the keys of the updates its seller has seen (see itemKey in changelog-utils).
// A seller who uses two computers is told once on each.

const WhatsNewContext = createContext(null);

const storageKeyFor = (userId) => (userId ? `voolist.whatsNew.seen.${userId}` : null);

function readSeenKeys (storageKey) {
  if (!storageKey) return new Set();
  try {
    return new Set(JSON.parse(localStorage.getItem(storageKey)) || []);
  } catch (error) {
    // Blocked or unreadable storage: nothing counts as seen.
    return new Set();
  }
}

// True when the browser saved the keys; false when its storage is blocked or full.
function writeSeenKeys (storageKey, keys) {
  if (!storageKey) return false;
  try {
    localStorage.setItem(storageKey, JSON.stringify(keys));
    return true;
  } catch (error) {
    return false;
  }
}

// ----------------------------------------------------------------------

export function WhatsNewProvider ({ children, entries = CHANGELOG }) {
  const { user } = useAuthContext();

  const storageKey = storageKeyFor(user?.id);

  const storedKeys = useMemo(() => readSeenKeys(storageKey), [storageKey]);

  // Marked seen during this visit; kept here too so a browser that blocks storage still clears the news.
  const [markedThisVisit, setMarkedThisVisit] = useState(null);

  const seenKeys = markedThisVisit?.storageKey === storageKey ? markedThisVisit.keys : storedKeys;

  const joinedDay = signupDay(user?.createdAt);

  const unseen = useMemo(
    () => (storageKey ? unseenItems(entries, seenKeys, joinedDay) : []),
    [entries, seenKeys, joinedDay, storageKey]
  );

  // Marks every update on the page as seen and says whether the browser saved it; either way they stay seen for
  // this visit. What the browser already remembers is kept, so a tab still running an older version of the page
  // never makes it forget newer updates.
  const markAllSeen = useCallback(() => {
    const pageKeys = entries.flatMap(entryItems).map((item) => item.key);
    const keys = [...new Set([...readSeenKeys(storageKey), ...pageKeys])];
    const saved = writeSeenKeys(storageKey, keys);
    setMarkedThisVisit({ storageKey, keys: new Set(keys) });
    return saved;
  }, [entries, storageKey]);

  const value = useMemo(() => ({ entries, unseen, markAllSeen }), [entries, unseen, markAllSeen]);

  return <WhatsNewContext.Provider value={value}>{children}</WhatsNewContext.Provider>;
}

WhatsNewProvider.propTypes = {
  children: PropTypes.node,
  entries: PropTypes.array,
};

export function useWhatsNew () {
  const context = useContext(WhatsNewContext);
  if (!context) throw new Error('useWhatsNew must be used inside WhatsNewProvider');
  return context;
}
